import {Component, Input, OnChanges, SimpleChanges} from '@angular/core';
import {MatSnackBar} from '@angular/material/snack-bar';
import {TranslateService} from '@ngx-translate/core';
import {BinaryData} from '../../../model/proposal/binary-data';
import {UploadLocation} from '../../../enum/upload-location';
import {FileUploadService} from '../../../services/file-upload-service/file-upload.service';

/**
 * Read-only file list used inside the Version History tab. Renders attached files
 * grouped by their upload location, and replaces the download button with a
 * "Deleted" badge for files that no longer exist in the latest proposal version.
 *
 * Mirrors the layout/grouping of {@link import('../../insurance-contract-details-elements/proposal-details-download/proposal-details-download.component').ProposalDetailsDownloadComponent}
 * but is intentionally separate to avoid leaking version-history concerns into the
 * generic file list components used elsewhere.
 */
@Component({
    selector: 'app-version-history-files',
    templateUrl: './version-history-files.component.html',
    styleUrls: ['./version-history-files.component.scss'],
    standalone: false
})
export class VersionHistoryFilesComponent implements OnChanges {

  @Input() attachedFiles: BinaryData[] = [];
  @Input() existingFileIds: Set<string> = new Set<string>();

  /**
   * Files grouped by upload location, preserving the same insertion order as
   * {@link ProposalDetailsDownloadComponent} (i.e. order of first appearance in
   * {@link attachedFiles}).
   */
  mappedAttachedFiles: Map<UploadLocation, BinaryData[]> = new Map<UploadLocation, BinaryData[]>();

  loadingStatus: Map<string, boolean> = new Map<string, boolean>();

  constructor(protected fileUploadService: FileUploadService,
              private readonly translate: TranslateService,
              private readonly snackBar: MatSnackBar) {
  }

  ngOnChanges(_changes: SimpleChanges): void {
    this.mappedAttachedFiles = this.groupByUploadLocation(this.attachedFiles ?? []);
  }

  private groupByUploadLocation(files: BinaryData[]): Map<UploadLocation, BinaryData[]> {
    const grouped = new Map<UploadLocation, BinaryData[]>();
    for (const file of files) {
      const bucket = grouped.get(file.uploadLocation);
      if (bucket == null) {
        grouped.set(file.uploadLocation, [file]);
      } else {
        bucket.push(file);
      }
    }
    return grouped;
  }

  isAvailable(file: BinaryData): boolean {
    return !!file && this.existingFileIds?.has(file.id) === true;
  }

  downloadFile(binaryData: BinaryData): void {
    if (!this.isAvailable(binaryData)) {
      return;
    }
    this.loadingStatus.set(binaryData.id, true);
    this.fileUploadService.downloadFile(binaryData.id, binaryData.secret, binaryData.name, binaryData.type).subscribe(
      () => this.loadingStatus.set(binaryData.id, false),
      _ => {
        this.loadingStatus.set(binaryData.id, false);
        this.snackBar.open(this.translate.instant('fileDownloadError'), '', {
          duration: 3000,
          panelClass: ['fail-snackbar', 'custom-snackbar']
        });
      }
    );
  }

  mapUploadLocation(uploadLocation: UploadLocation): string | undefined {
    switch (uploadLocation) {
      case UploadLocation.PREVIOUSDAMAGE:
        return 'previousDamages';
      case UploadLocation.INDIVIDUALCLAUSESCONDITIONS:
        return 'individualClausesConditions';
      case UploadLocation.PROPOSALINFORMATION:
        return 'proposalInformation';
      case UploadLocation.RISKDESCRIPTION:
        return 'riskDescription';
      case UploadLocation.RISKLOCATION:
        return 'riskLocation';
      default:
        return undefined;
    }
  }

  trackByFileId(_index: number, file: BinaryData): string {
    return file?.id ?? String(_index);
  }

  /** keyvalue pipe comparator preserving the Map's insertion order. */
  originalOrder = (_a: unknown, _b: unknown): number => 0;
}
